import React, { useState, useEffect } from "react";
import { Card, Table, Select, Button, Modal, Space, Spin, message } from "antd";
import { DownloadOutlined } from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";

const { Option } = Select;

function ReceivedDocuments() {
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [documents, setDocuments] = useState([]); // array of documents
  const [loading, setLoading] = useState(false);
  const [docLoading, setDocLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchDepartments();
    fetchEmployees(null, true); // only documents
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await axiosInstance.get(API.department.getList);

      setDepartments(res.data);
    } catch (err) {
      console.error("❌ Error fetching departments:", err);
      message.error("Failed to load departments");
    }
  };

  // Fetch Employees
  const fetchEmployees = async (departmentId = null, hasDocuments = true) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(API.employee.getAll(departmentId, hasDocuments));

      setEmployees(res.data);
    } catch (err) {
      console.error("❌ Error fetching employees:", err);
      message.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Documents for a User
  const handleViewDocuments = async (employeeId) => {
    setDocLoading(true);
    try {
      const res = await axiosInstance.get(API.hiringContracts.getByUserId(employeeId));

      if (res?.data?.detail) {
        message.error(res.data.detail);
        setDocLoading(false);
        return;
      }

      const data = res.data;

      // Convert API data into displayable format
      const documentList = [
        { name: "NDA File", file: data.ndafile },
        { name: "NCA File", file: data.ncafile },
        { name: "Employee Agreement", file: data.employeeAgreement },
        { name: "I-9 Form", file: data.i_nine },
        { name: "W-4 Form", file: data.w_four },
        { name: "Offer Letter", file: data.offerLetter },
        { name: "HR Policy", file: data.hrPolicy },
      ].filter((doc) => doc.file); // Only non-empty docs

      setDocuments(documentList);
      setIsModalOpen(true);
    } catch (err) {
      console.error(" Error fetching documents:", err);
      message.error("Failed to load documents.");
    } finally {
      setDocLoading(false);
    }
  };

  // Handlers for View / Download
  // Base file URL (no /api at the end)
  const fileBaseUrl = process.env.REACT_APP_Base_URL;

  //  View document in new tab
  const handlePdfPreview = (fileUrl) => {
    if (!fileUrl) return message.warning("No file found");

    const cleanUrl = `${fileBaseUrl}/${fileUrl.replace(/^\/+/, "")}`;

    window.open(cleanUrl, "_blank");
  };

  //  Download document
  const handleDownload = (fileUrl, fileName) => {
    if (!fileUrl) {
      message.warning("No file to download");
      return;
    }

    try {
      // Directly trigger file download
      const link = document.createElement("a");
      link.href = fileUrl; // same URL used for preview
      link.setAttribute("download", fileName || "document");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success(" Download started!");
    } catch (error) {
      console.error("Download failed:", error);
      message.error("Failed to download file");
    }
  };

  // Table Columns
  const employeeColumns = [
    {
      title: "Employee Name",
      dataIndex: "firstName",
      key: "firstName",
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button className="btn-primary" onClick={() => handleViewDocuments(record.id)}>
          View Documents
        </Button>
      ),
    },
  ];

  const documentColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => handlePdfPreview(record.file)}>
            View
          </Button>
          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record.file, record.name)}
          >
            Download
          </Button>
        </Space>
      ),
    },
  ];

  // JSX

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="body flex-grow-1 px-3">
        <Card title="Employee Received Documents" className="card-professional">
          {/* Department Select */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ marginRight: 10 }}>Select Department:</label>
            <Select
              style={{ width: 300 }}
              placeholder="Choose Department"
              onChange={(value) => {
                setSelectedDept(value);
                fetchEmployees(value, true); // filtered + hasDocuments
              }}
              value={selectedDept}
            >
              {departments.map((dept) => (
                <Option key={dept.id} value={dept.id}>
                  {dept.departmentName || dept.name || `Department ${dept.id}`}
                </Option>
              ))}
            </Select>
          </div>

          {/* Employees Table */}
          {loading ? (
            <Spin />
          ) : (
            <Table
              dataSource={employees}
              columns={employeeColumns}
              pagination={false}
              rowKey="id"
            />
          )}
        </Card>

        {/* Documents Modal */}
        <Modal
          title="Employee Documents"
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          width={700}
        >
          {docLoading ? (
            <Spin />
          ) : documents.length > 0 ? (
            <Table
              dataSource={documents}
              columns={documentColumns}
              rowKey="name"
              pagination={false}
            />
          ) : (
            <p>No documents found for this user.</p>
          )}
        </Modal>
      </div>
    </DashboardLayout>
  );
}

export default ReceivedDocuments;
